import { useEffect, useRef } from 'react';
import { useThemePrefsStore } from '@/stores/useThemePrefsStore';
import { useAccessibilityPrefsStore, type AccessibilityPrefs } from '@/stores/useAccessibilityPrefsStore';
import { useMenuPrefsStore } from '@/stores/useMenuPrefsStore';
import { useUserPreferences, useSaveUserPreferences } from './useUserPreferences';

/**
 * Syncs theme and accessibility preferences between local Zustand stores
 * and the server-side user_preferences table.
 *
 * - On mount: loads saved prefs from DB and applies them to local stores.
 * - On change: persists local store changes back to the DB.
 *
 * Mount this hook once in a top-level authenticated layout component.
 */
export const useSyncUserPreferences = () => {
  const { data: userPrefs } = useUserPreferences();
  const { mutate: savePrefs } = useSaveUserPreferences();
  const loadedRef = useRef(false);

  // Theme store
  const themeMode = useThemePrefsStore((s) => s.themeMode);
  const setThemeMode = useThemePrefsStore((s) => s.setThemeMode);

  // Accessibility store
  const fontScale = useAccessibilityPrefsStore((s) => s.fontScale);
  const fontFamily = useAccessibilityPrefsStore((s) => s.fontFamily);
  const lineHeightLoose = useAccessibilityPrefsStore((s) => s.lineHeightLoose);
  const wideLetterSpacing = useAccessibilityPrefsStore((s) => s.wideLetterSpacing);
  const underlineLinks = useAccessibilityPrefsStore((s) => s.underlineLinks);
  const highlightFocus = useAccessibilityPrefsStore((s) => s.highlightFocus);
  const reduceMotion = useAccessibilityPrefsStore((s) => s.reduceMotion);
  const setFontScale = useAccessibilityPrefsStore((s) => s.setFontScale);
  const setFontFamily = useAccessibilityPrefsStore((s) => s.setFontFamily);
  const setLineHeightLoose = useAccessibilityPrefsStore((s) => s.setLineHeightLoose);
  const setWideLetterSpacing = useAccessibilityPrefsStore((s) => s.setWideLetterSpacing);
  const setUnderlineLinks = useAccessibilityPrefsStore((s) => s.setUnderlineLinks);
  const setHighlightFocus = useAccessibilityPrefsStore((s) => s.setHighlightFocus);
  const setReduceMotion = useAccessibilityPrefsStore((s) => s.setReduceMotion);

  // Menu favorites store
  const favorites = useMenuPrefsStore((s) => s.favorites);
  const setFavorites = useMenuPrefsStore((s) => s.setFavorites);

  // Load from DB on first successful fetch
  useEffect(() => {
    if (!userPrefs || loadedRef.current) return;
    loadedRef.current = true;

    if (userPrefs.theme_mode) {
      setThemeMode(userPrefs.theme_mode);
    }

    if (userPrefs.accessibility) {
      const a = userPrefs.accessibility;
      if (a.fontScale != null) setFontScale(a.fontScale);
      if (a.fontFamily != null) setFontFamily(a.fontFamily);
      if (a.lineHeightLoose != null) setLineHeightLoose(a.lineHeightLoose);
      if (a.wideLetterSpacing != null) setWideLetterSpacing(a.wideLetterSpacing);
      if (a.underlineLinks != null) setUnderlineLinks(a.underlineLinks);
      if (a.highlightFocus != null) setHighlightFocus(a.highlightFocus);
      if (a.reduceMotion != null) setReduceMotion(a.reduceMotion);
    }

    if (userPrefs.menu_favorites && userPrefs.menu_favorites.length > 0) {
      setFavorites(userPrefs.menu_favorites);
    }
  }, [userPrefs, setThemeMode, setFontScale, setFontFamily, setLineHeightLoose, setWideLetterSpacing, setUnderlineLinks, setHighlightFocus, setReduceMotion, setFavorites]);

  // Save theme mode to DB on change
  const prevThemeRef = useRef(themeMode);
  useEffect(() => {
    if (!loadedRef.current) return;
    if (prevThemeRef.current === themeMode) return;
    prevThemeRef.current = themeMode;
    savePrefs({ theme_mode: themeMode });
  }, [themeMode, savePrefs]);

  // Save menu favorites to DB on change
  const prevFavoritesRef = useRef<string>(JSON.stringify(favorites));
  useEffect(() => {
    if (!loadedRef.current) return;
    const serialized = JSON.stringify(favorites);
    if (prevFavoritesRef.current === serialized) return;
    prevFavoritesRef.current = serialized;
    savePrefs({ menu_favorites: favorites });
  }, [favorites, savePrefs]);

  // Save accessibility prefs to DB on change
  const currentA11y: AccessibilityPrefs = {
    fontScale,
    fontFamily,
    lineHeightLoose,
    wideLetterSpacing,
    underlineLinks,
    highlightFocus,
    reduceMotion,
  };
  const prevA11yRef = useRef<string>(JSON.stringify(currentA11y));
  useEffect(() => {
    if (!loadedRef.current) return;
    const serialized = JSON.stringify(currentA11y);
    if (prevA11yRef.current === serialized) return;
    prevA11yRef.current = serialized;
    savePrefs({ accessibility: currentA11y });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontScale, fontFamily, lineHeightLoose, wideLetterSpacing, underlineLinks, highlightFocus, reduceMotion, savePrefs]);
};
